import { Controller } from "@hotwired/stimulus";
import {
  create,
  parseCreationOptionsFromJSON,
} from "@github/webauthn-json/browser-ponyfill";
import { jsonHeaders } from "./passkey_csrf";

// Connects to data-controller="passkey-registration"
// Values: optionsUrl (registration_options), createUrl (POST), redirectUrl
// Targets: nickname, status, button
export default class extends Controller {
  static values = {
    optionsUrl: String,
    createUrl: String,
    redirectUrl: String,
  };
  static targets = ["nickname", "status", "button"];

  async register(event) {
    event.preventDefault();
    if (!window.PublicKeyCredential) {
      this.setStatus("This device does not support passkeys.", true);
      return;
    }

    this.disable(true);
    this.setStatus("Follow your device prompt…");

    try {
      const optionsRes = await fetch(this.optionsUrlValue, {
        headers: jsonHeaders(),
        credentials: "same-origin",
      });
      if (!optionsRes.ok) throw new Error("Could not start registration.");
      const { options, ceremony_id } = await optionsRes.json();

      const credential = await create(
        parseCreationOptionsFromJSON({ publicKey: options }),
      );

      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          publicKeyCredential: credential,
          ceremony_id,
          nickname: this.hasNicknameTarget
            ? this.nicknameTarget.value.trim()
            : "",
        }),
      });

      if (res.ok) {
        this.setStatus("Passkey registered.");
        setTimeout(() => Turbo.visit(this.redirectUrlValue), 800);
      } else {
        const body = await res.json().catch(() => ({}));
        this.setStatus(
          this.errorsToText(body.errors) || "Registration failed.",
          true,
        );
        this.disable(false);
      }
    } catch (err) {
      this.setStatus(this.friendlyError(err), true);
      this.disable(false);
    }
  }

  friendlyError(err) {
    switch (err.name) {
      case "NotAllowedError":
        return "Request was cancelled or timed out.";
      case "InvalidStateError":
        return "This device already has a passkey for this account.";
      case "NotSupportedError":
        return "This device does not support passkeys.";
      default:
        return err.message || "Could not register passkey.";
    }
  }

  errorsToText(errors) {
    if (!errors) return null;
    return Object.values(errors).flat().join(", ");
  }

  setStatus(msg, isError = false) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = msg;
    this.statusTarget.classList.toggle("text-red-600", isError);
  }

  disable(state) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = state;
  }
}
